import { Component, EventEmitter, Input, Output } from '@angular/core';
import {
  Datagrid,
  DatagridColumn,
  DatagridFormat,
  DatagridTooltip,
  DatagridTextWithIconConfiguration,
  RowCondition,
} from '@zipari/shared-ds-util-datagrid';
import { FormattingService } from '../../../shared/services/formatting.service';
import { getValue } from '../../../shared/utils/get-value';
import { DatagridDigitalDataService } from '../datagrid-digital-data.service';

@Component({
  selector: 'datagrid-cell-options',
  templateUrl: './datagrid-cell-options.component.html',
  styleUrl: './datagrid-cell-options.component.scss',
})
export class DatagridCellOptionsComponent {
  @Input() config: Datagrid;
  @Input() column: DatagridColumn;
  @Input() row: object;
  @Input() rowIndex: number;
  @Input() condition: RowCondition;
  @Input() dataGridId: string;
  @Output() actionClicked = new EventEmitter<object>();
  @Output() datagridRowSelected = new EventEmitter<any>();
  @Output() datagridSsoRowSelected = new EventEmitter<any>();
  formats = DatagridFormat;

  constructor(
    private formattingService: FormattingService,
    public digitalDataGridService: DatagridDigitalDataService,
  ) {}

  /**
   * Gets type of value (it can be of type: string, number, object etc)
   * @param {any} value
   * @returns type of value
   */
  getTypeOf(value: any): any {
    return typeof value;
  }

  /**
   * Gets restructured value based on value, column and type details
   * @param {string} value
   * @param {DatagridColumn} column
   * @param {string} type
   * @returns formatted value
   */
  getFormattedObjectValue(
    value: string,
    column: Partial<DatagridColumn>,
    type: string,
  ): string {
    return this.formattingService.restructureValueBasedOnFormat(
      value,
      column,
      type,
    );
  }

  /**
   * Gets tooltip text based on value and column details
   * @param {string} value
   * @param {DatagridColumn} column
   * @returns formatted tooltip text
   */
  getFormattedTooltipObjectValue(
    value: DatagridTextWithIconConfiguration,
    column: Partial<DatagridColumn>,
  ): DatagridTooltip {
    return {
      description: this.formattingService.restructureValueBasedOnFormat(
        value.tooltip,
        column,
        'TEXT',
      ),
      leftAlign: value.leftAlign || false,
      directionTop: value.directionTop || false,
    };
  }

  getFormattedValue(row: object, column: Partial<DatagridColumn>): any {
    const formattedValue = this.formattingService.restructureValueBasedOnFormat(
      getValue(row, column?.prop),
      column,
    );

    return formattedValue === 0 ? '0' : formattedValue;
  }

  getStatusLabel(column: any, rowStatusValue: string) {
    if (column?.statuses) {
      return (
        []
          .concat(
            ...Object.keys(column.statuses).map(
              (statusKey) => column.statuses[statusKey],
            ),
          )
          .find((status) => status.value === rowStatusValue)?.label ||
        rowStatusValue
      );
    }

    return rowStatusValue;
  }

  isClickable(row, condition) {
    if (!row || !condition?.key) {
      return false;
    }

    return row[condition?.key] && row[condition?.key] === condition?.val;
  }

  onActionClicked(row: any, action: string, clickEvent?: Event) {
    this.actionClicked.emit({ row, action, clickEvent });
  }

  viewDetail(row, columnProp: string, routeToProp?: string) {
    this.datagridRowSelected.emit({ row, columnProp, routeToProp });
  }

  viewSSODetail(row, columnRoute: string, columnOptions) {
    this.datagridSsoRowSelected.emit({ row, columnRoute, columnOptions });
  }
}
